import { useEffect, useRef, useState } from "react";
import Game from "../../store/Game";
import useWindowSize from "../../hooks/useWindowSize";
import INTOUCH_API from "../../services/Api";
import useCurrentGame from "../../hooks/useCurrentGame";

const MOBILE_VIEW_COUNT_CARDS = 8;
const DESKTOP_VIEW_COUNT_CARDS = 15;
const LAPTOP_VIEW_COUNT_CARDS = 16;
const TABLET_VIEW_COUNT_CARDS = 18;

const LAPTOP_WIDTH_BORDER = 1217;
const TABLET_WIDTH_BORDER = 974;
const MOBILE_WIDTH_BORDER = 622;

export default function useViewController() {
  const [cards, setCards] = useState([]);
  const [sizeToShow, setSizeToShow] = useState(() => DESKTOP_VIEW_COUNT_CARDS);
  // 24.09: эффект загрузки не зависит от sizeToShow, поэтому в его колбэке
  // значение из замыкания устаревшее (стартовые 15). У демо превью бывает до 30
  // карточек — часть просто не показывалась бы, а раскрыть их нельзя: кнопка у
  // демо открывает попап. Читаем актуальное значение через ref.
  const sizeToShowRef = useRef(sizeToShow);
  sizeToShowRef.current = sizeToShow;
  const { windowSize } = useWindowSize();
  const { getCurrentGame } = useCurrentGame();
  // 17.09: localStorage может быть пуст (сюда пускают по Game.game из MobX) —
  // берём стор как запасной источник, иначе и признак DEMO определится неверно,
  // и запрос форм ниже упадёт на currentGame.id
  const currentGame = () => getCurrentGame() || Game.game;
  // игра не куплена (DEMO) — тот же признак, что и в результатах
  const isDemoGame = currentGame()?.type === "DEMO";

  // 21.09 (решение клиента): «раздел Игра — источник карточек для Выбрать тему».
  // Пока зоны пустые (тест не раскидан обоими — бэк не отдаёт зоны, если хоть у
  // одного не все 88), карточек здесь нет вовсе, и страница показывает описание.
  // У некупленной игры видны ровно те же карточки, что открыты в «Игре», —
  // превью-срез каждой зоны, 5 в вебе и 4 на мобайле.
  const PREVIEW_PER_ZONE = windowSize.width < 623 ? 4 : 5;
  const realZones = Game.zones.filter((z) => z.kind !== "personal");
  const hasZoneCards = realZones.some((z) => (z.cards || []).length > 0);
  const previewQuestionIds = new Set(
    realZones.flatMap((z) =>
      (z.cards || []).slice(0, PREVIEW_PER_ZONE).map((c) => c.questionId)
    )
  );

  // зоны грузит страница результатов; сюда можно прийти и мимо неё (бургер,
  // прямая ссылка), поэтому подтягиваем сами. Условие — «зоны загружены НЕ для
  // этой игры»: проверять по наличию карточек нельзя, иначе после смены игры
  // сойдут чужие зоны, а пустой ответ («тест не пройден») перезапрашивался бы
  // бесконечно.
  const gameId = currentGame()?.id;
  const zonesReady = gameId != null && Game.zonesGameId === gameId;

  useEffect(() => {
    if (gameId && !zonesReady && !Game.isZonesLoading) {
      Game.getZones(gameId);
    }
    // isZonesLoading и zonesGameId В зависимостях намеренно: если на момент
    // монтирования шла загрузка зон ДРУГОЙ игры, мы её пропускаем, и без этих
    // зависимостей эффект больше не перезапустится — экран залипнет на спиннере
    // (zonesReady так и останется false). Компонент — observer, поэтому чтение
    // этих observable в рендере делает перезапуск корректным.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, zonesReady, Game.isZonesLoading, Game.zonesGameId]);

  // ключ превью: меняется, когда доехали/сменились зоны — по нему пересобираем
  // список. Для купленной игры срез не применяется, поэтому ключ не нужен.
  const previewKey = isDemoGame ? [...previewQuestionIds].sort().join(",") : "";
  // порядковый номер загрузки: ответы старых цепочек игнорируем, иначе
  // «дозонная» цепочка с пустым previewQuestionIds может прийти последней и
  // затереть список пустым — спиннер навсегда
  const fetchSeq = useRef(0);

  useEffect(() => {
    if (!zonesReady) return;
    const seq = ++fetchSeq.current;
    const isStale = () => seq !== fetchSeq.current;
    Game.getCards("questions_cards", false, (response) => {
      if (isStale()) return;
      if (response && response.data) {
        const game = currentGame();
        if (!game?.id) return;
        const proceed = (allCards) => {
          INTOUCH_API.requestManager(
            "getInsightForms",
            { payload: game.id },
            (responseForms) => {
              if (isStale()) return;
              INTOUCH_API.requestManager("getInsightFormDrafts", {}, (res) => {
                if (isStale()) return;
                const draftHasNotAnswers = res.data.filter(
                  (element) =>
                    !element.questions.some(
                      (answer) =>
                        !!answer.value.length && answer.value !== ":week"
                    )
                );

                const insightFormIds = draftHasNotAnswers.map((d) => d.insightFormId);

                const filteredForm = responseForms.data.filter(
                  (form) => !form.some((item) => insightFormIds.includes(item.id))
                );

                const cards = allCards.filter((card) => {
                  const existForm = filteredForm.some((form) => {
                    return form.some(
                      (question) => question.questionId === card.id
                    );
                  });
                  if (existForm) return false;
                  // у некупленной игры — только превью-срез из «Игры»;
                  // у купленной набор не режем
                  return isDemoGame ? previewQuestionIds.has(card.id) : true;
                });
                setCards(
                  cards.map((card, index) => ({
                    ...card,
                    // у демо список уже урезан зонами — прячем только у купленной
                    isHide: isDemoGame ? false : index >= sizeToShowRef.current,
                  }))
                );
              });
            },
            () => {}
          );
        };
        // +57: у игры с разблокированным набором карточки расширенного набора
        // тоже доступны как темы анкеты (бэк отдаёт наборы только по одному)
        if (currentGame()?.hasExtendedSet) {
          INTOUCH_API.requestManager(
            "questions_cards",
            { setType: "EXTENDED_57" },
            (extRes) => proceed([...response.data, ...(extRes?.data || [])]),
            () => proceed(response.data)
          );
        } else {
          proceed(response.data);
        }
      }
    });
  }, [zonesReady, isDemoGame, previewKey]);

  useEffect(() => {
    // 21.09: у некупленной игры список уже урезан до превью-среза зон, поэтому
    // второй раз резать его по ширине не надо — показываем целиком.
    if (isDemoGame) {
      if (sizeToShow !== Infinity) setSizeToShow(Infinity);
      return;
    }
    if (windowSize.width <= MOBILE_WIDTH_BORDER) {
      if (sizeToShow === MOBILE_VIEW_COUNT_CARDS) return;
      setSizeToShow(MOBILE_VIEW_COUNT_CARDS);
    } else if (windowSize.width <= TABLET_WIDTH_BORDER) {
      if (sizeToShow === TABLET_VIEW_COUNT_CARDS) return;
      setSizeToShow(TABLET_VIEW_COUNT_CARDS);
    } else if (windowSize.width > LAPTOP_WIDTH_BORDER) {
      if (sizeToShow === DESKTOP_VIEW_COUNT_CARDS) return;
      setSizeToShow(DESKTOP_VIEW_COUNT_CARDS);
    } else {
      if (sizeToShow === LAPTOP_VIEW_COUNT_CARDS) return;
      setSizeToShow(LAPTOP_VIEW_COUNT_CARDS);
    }
  }, [windowSize.width, sizeToShow, isDemoGame]);

  useEffect(() => {
    setCards((prevState) =>
      prevState.map((card, index) => ({
        ...card,
        isHide: index >= sizeToShow,
      }))
    );
  }, [sizeToShow]);

  const showMore = () => {
    setCards((prevState) =>
      prevState.map((card) => ({ ...card, isHide: false }))
    );
  };
  const showLess = () => {
    setCards((prevState) =>
      prevState.map((card, index) => ({ ...card, isHide: index >= sizeToShow }))
    );
  };

  return {
    cards,
    isDemoGame,
    hasZoneCards,
    zonesReady,
    zonesLoadFailed: Game.zonesLoadFailed,
    retryZones: () => gameId && Game.getZones(gameId),
    showMore,
    showLess,
  };
}
